import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { RoleIcon } from '@/components/common/RoleIcon';
import { useRole } from '@/hooks/useRole';
import { useGlobalSelector } from '@/hooks/useGlobalState';
import { displayName, webRolesOf } from '@/auth/userAccess';
import { PORTAL_HOME_PATH, PORTAL_LABELS, PORTALS, type Portal, type RoleCode } from '@/routes/roles';
import React from 'react';

interface RoleSwitcherProps {
  /** Icon-only rail mode — shows just the avatar. */
  collapsed?: boolean;
}

/** One choice in the switcher: a role by its code, with its Fabrica name and icon and the portal it opens in. */
interface RoleChoice {
  code: RoleCode;
  name: string;
  icon: string | null;
  portal: Portal;
  /** The page it opens on: its Fabrica web home (the portal's home in sample data). */
  home: string;
}

/** Sample data only (no API roles): each portal's icon, by MUI icon name like Fabrica's role icons. */
const SAMPLE_ICONS: Record<Portal, string> = {
  administrator: 'AdminPanelSettingsOutlined',
  principal: 'WorkspacePremiumOutlined',
  faculty: 'CastForEducationOutlined',
  student: 'BackpackOutlined',
  parent: 'FamilyRestroomOutlined',
};

/** The line under a role's name: the portal it opens in. */
const portalLine = (portal: Portal) => `${PORTAL_LABELS[portal]} portal`;

export function RoleSwitcher({ collapsed = false }: RoleSwitcherProps) {
  const { roleCode, roleLabel, setRole } = useRole();
  const access = useGlobalSelector((s) => s.access);
  const menus = useGlobalSelector((s) => s.menus);
  const navigate = useNavigate();
  const theme = useTheme();
  const sidebar = theme.tokens.component.sidebar;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');

  const close = () => {
    setAnchor(null);
    setQuery('');
  };

  const choose = (next: RoleChoice) => {
    close();
    if (next.code === roleCode) return;
    setRole(next.code);
    void navigate(next.home, { replace: true });
  };

  // Signed in: the person's roles that open on the web, in the API's names and icons. Sample data: the portals.
  const allowed = useMemo<RoleChoice[]>(
    () =>
      access
        ? webRolesOf(access, menus).map(({ code, name, icon, portal, home }) => ({ code, name, icon, portal, home }))
        : PORTALS.map((p) => ({ code: p, name: PORTAL_LABELS[p], icon: SAMPLE_ICONS[p], portal: p, home: PORTAL_HOME_PATH[p] })),
    [access, menus],
  );

  const filteredRoles = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return allowed;
    return allowed.filter(
      (r) => r.name.toLowerCase().includes(term) || portalLine(r.portal).toLowerCase().includes(term),
    );
  }, [query, allowed]);

  const trigger = (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Account menu"
      aria-haspopup="menu"
      aria-expanded={anchor ? 'true' : undefined}
      onClick={(e) => setAnchor(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setAnchor(e.currentTarget);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        mx: 1,
        px: collapsed ? 0 : 1,
        py: 0.5,
        borderRadius: 1.5,
        cursor: 'pointer',
        justifyContent: collapsed ? 'center' : 'flex-start',
        '&:hover': { bgcolor: sidebar.hoverBackground },
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          fontSize: '0.875rem',
          fontWeight: 700,
          color: 'primary.contrastText',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        }}
      >
        {roleLabel[0]}
      </Avatar>
      {!collapsed && (
        <>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 700, color: sidebar.text }}>
              {access ? displayName(access) : 'Demo User'}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
                px: 0.75,
                borderRadius: 999,
                fontSize: '0.6875rem',
                fontWeight: 700,
                lineHeight: 1.7,
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
              {roleLabel}
            </Box>
          </Box>
          <Box
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              border: `1px solid ${sidebar.border}`,
              color: sidebar.mutedText,
            }}
          >
            <UnfoldMoreRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <React.Fragment>
      {collapsed ? (
        <Tooltip title={`Account · ${roleLabel}`} placement="right">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 250, maxHeight: 400 } } }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.75, pt: 0.5, pb: 1 }}
        >
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <GroupsOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }}>
              SWITCH ROLE
            </Typography>
          </Stack>
          <Chip
            label={allowed.length}
            size="small"
            sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: 'action.selected' }}
          />
        </Stack>

        <Box sx={{ px: 1, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Find a role"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999, bgcolor: 'action.hover' } }}
          />
        </Box>

        <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 1, pb: 0.5 }}>
          {filteredRoles.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1.75, py: 2 }}>
              No roles match “{query.trim()}”.
            </Typography>
          )}
          {filteredRoles.map((r) => {
            const active = r.code === roleCode;
            return (
              <MenuItem
                key={r.code}
                selected={active}
                onClick={() => choose(r)}
                sx={{
                  borderRadius: 1.5,
                  mx: 0.5,
                  my: 0.375,
                  py: 1,
                  gap: 1.25,
                  border: '1px solid transparent',
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    borderColor: alpha(theme.palette.success.main, 0.4),
                    '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.14) },
                  },
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    boxShadow: 'none',
                  }}
                >
                  <RoleIcon icon={r.icon} fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: active ? 700 : 600 }}>
                    {r.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {portalLine(r.portal)}
                  </Typography>
                </Box>
                {active && <CheckCircleRoundedIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </React.Fragment>
  );
}
